import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscription'
import { useUiStore } from '@/stores/ui'
import { useSEO } from '@/composables/useSEO'
import LoginView from '../views/LoginView.vue'
import RegisterView from '../views/RegisterView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import VerifyEmailView from '../views/VerifyEmailView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/MarketingHomeView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/features',
      name: 'features',
      component: () => import('../views/pages/FeaturesView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('../views/pages/PricingView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/solutions',
      name: 'solutions',
      component: () => import('../views/pages/SolutionsView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
      meta: { requiresGuest: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: { requiresGuest: true }
    },
    {
      path: '/verify-email/:token?',
      name: 'verify-email',
      component: VerifyEmailView,
      meta: { requiresGuest: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/pos',
      name: 'pos',
      component: () => import('../views/POSView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'], feature: 'pos' }
    },
    {
      path: '/marketplace',
      redirect: '/stores'
    },
    {
      path: '/stores',
      name: 'store-directory',
      component: () => import('../views/MarketplaceView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/marketplace/track',
      name: 'marketplace-track',
      component: () => import('../views/MarketplaceTrackView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/catalog',
      name: 'catalog',
      component: () => import('../views/CustomerCatalogView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/inventory',
      name: 'inventory',
      component: () => import('../views/InventoryView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'], feature: 'inventory' }
    },
    {
      path: '/products',
      name: 'products',
      component: () => import('../views/ProductsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'], feature: 'products' }
    },
    {
      path: '/categories',
      name: 'categories',
      component: () => import('../views/CategoriesView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../views/ReportsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'], feature: 'reports' }
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/OrdersView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'], feature: 'orders' }
    },
    {
      path: '/services',
      name: 'services',
      component: () => import('../views/ServicesView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/service-requests',
      redirect: { path: '/services', query: { tab: 'requests' } }
    },
    {
      path: '/orders/:id',
      name: 'order-details',
      component: () => import('../views/OrderDetailsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'], feature: 'orders' }
    },
    {
      path: '/business-balances',
      name: 'business-balances',
      component: () => import('../views/BusinessBalancesView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => import('../views/PaymentsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../views/SettingsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/storefront-manager',
      name: 'storefront-manager',
      component: () => import('../views/SettingsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'], feature: 'storefront', settingsSection: 'storefront' }
    },
    {
      path: '/tax',
      name: 'tax-management',
      component: () => import('../views/TaxManagementView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'], feature: 'etims' }
    },
    {
      path: '/tax-config',
      redirect: '/tax'
    },
    {
      path: '/subscription',
      name: 'subscription',
      component: () => import('../views/SubscriptionView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/CustomersView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/cashiers',
      name: 'cashiers',
      component: () => import('../views/CashiersView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/NotificationsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/fraud',
      name: 'fraud',
      component: () => import('../views/FraudFlagsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/super-admin',
      name: 'super-admin',
      component: () => import('../views/SuperAdminDashboard.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/leads',
      name: 'super-admin-leads',
      component: () => import('../views/SuperAdminLeadsView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/crm',
      name: 'super-admin-crm',
      component: () => import('../views/SuperAdminCrmView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/crm/tasks',
      name: 'super-admin-crm-tasks',
      component: () => import('../views/SuperAdminCrmTasksView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/crm/leads/:id',
      name: 'super-admin-crm-lead-detail',
      component: () => import('../views/SuperAdminCrmLeadDetailView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/leads/acquisition',
      name: 'super-admin-lead-acquisition',
      component: () => import('../views/SuperAdminLeadAcquisitionView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/whatsapp-onboarding',
      name: 'super-admin-whatsapp-onboarding',
      component: () => import('../views/SuperAdminWhatsappOnboardingView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/businesses',
      name: 'super-admin-businesses',
      component: () => import('../views/BusinessManagementView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/marketplace',
      name: 'super-admin-marketplace',
      component: () => import('../views/SuperAdminMarketplaceView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/fraud',
      name: 'super-admin-fraud',
      component: () => import('../views/SuperAdminFraudView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/marketing-analytics',
      name: 'super-admin-marketing-analytics',
      component: () => import('../views/SuperAdminMarketingAnalyticsView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/super-admin/subscriptions',
      name: 'subscription-config',
      component: () => import('../views/SubscriptionConfigView.vue'),
      meta: { requiresAuth: true, roles: ['super_admin'] }
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import('../views/pages/FaqView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('../views/pages/HelpCenterView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('../views/pages/ContactView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/whatsapp-support',
      name: 'whatsapp-support',
      component: () => import('../views/pages/WhatsAppSupportView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/pages/PrivacyPolicyView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/pages/TermsOfServiceView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/security',
      name: 'security',
      component: () => import('../views/pages/SecurityView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: { requiresAuth: false }
    },
    // SEO-friendly product routes
    {
      path: '/products/:id',
      name: 'product-detail',
      component: () => import('../views/ProductDetailView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/category/:category',
      name: 'category-products',
      component: () => import('../views/CategoryProductsView.vue'),
      meta: { requiresAuth: false }
    },
    // Demo and Marketing redirects
    {
      path: '/demo',
      redirect: '/catalog'
    },
    {
      path: '/kijani-pantry',
      redirect: '/catalog'
    },
    // Redirects for SEO
    {
      path: '/signin',
      redirect: '/login'
    },
    {
      path: '/signup',
      redirect: '/register'
    },
    {
      path: '/shop',
      redirect: '/catalog'
    },
    {
      path: '/store',
      redirect: '/catalog'
    },
    // Per-vendor storefront
    {
      path: '/s/:slug',
      name: 'storefront',
      component: () => import('../views/StorefrontView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/s/:slug/product/:productId',
      name: 'storefront-product',
      component: () => import('../views/StorefrontProductView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/s/:slug/invoice/:invoiceToken',
      name: 'storefront-invoice',
      component: () => import('../views/StorefrontInvoiceView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/s/requests/:requestId',
      name: 'storefront-service-request',
      component: () => import('../views/StorefrontServiceRequestView.vue'),
      meta: { requiresAuth: false }
    },
    // 404 page
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue')
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const uiStore = useUiStore()

  if (to.fullPath !== from.fullPath) {
    uiStore.startRouteLoading()
  }

  // Update SEO based on route
  if (typeof window !== 'undefined') {
    const routeName = String(to.name || '')
    const storefrontOwnedSeoRoutes = new Set([
      'storefront',
      'storefront-product',
      'storefront-invoice',
      'storefront-service-request',
    ])

    if (!storefrontOwnedSeoRoutes.has(routeName)) {
      const { updateSEO } = useSEO(routeName)
      updateSEO()
    }
  }

  // Wait for auth hydration before evaluating route access.
  if (!authStore.initialized) {
    try {
      await authStore.ensureInitialized()
    } catch (error) {
      console.error('Router guard auth initialization error:', error)
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    if (authStore.isSuperAdmin) {
      next('/super-admin')
    } else if (authStore.isAdminVendor) {
      next('/dashboard')
    } else if (authStore.isSalesVendor) {
      next('/pos')
    } else if (authStore.isCustomer) {
      next('/catalog')
    }
    return
  }

  // Vendor accounts always retain the Free foundation. Navigation is gated by
  // individual premium features, never by a global subscription paywall.
  const vendorRequiresSubscriptionCheck =
    authStore.isAuthenticated &&
    (authStore.isAdminVendor || authStore.isSalesVendor)

  if (vendorRequiresSubscriptionCheck) {
    try {
      const subStore = useSubscriptionStore()
      if (!subStore.status) {
        await subStore.fetchStatus()
      }

      const { useFeatureGate } = await import('@/composables/useFeatureGate')
      const featureGate = useFeatureGate()
      const requiredFeature = to.meta.feature as string | undefined

      if (requiredFeature) {
        if (!featureGate.loaded.value) {
          await featureGate.fetchGates()
        }

        if (featureGate.isLocked(requiredFeature)) {
          next({ path: '/subscription', query: { feature: requiredFeature }, replace: true })
          return
        }
      } else if (!featureGate.loaded.value) {
        featureGate.fetchGates().catch(() => { })
      }
    } catch (e) {
      // Do not make vendor operations unavailable because an entitlement lookup
      // temporarily failed. The backend remains authoritative for paid actions.
      console.warn('Feature entitlement check failed in router guard:', e)
    }
  }

  // Check role-based access
  if (to.meta.roles && authStore.user && Array.isArray(to.meta.roles)) {
    // Special handling for super_admin role
    if (to.meta.roles.includes('super_admin') && !authStore.isSuperAdmin) {
      next('/unauthorized')
      return
    }
    // For non-super-admin roles, check the actual user role
    else if (!to.meta.roles.includes('super_admin') && !to.meta.roles.includes(authStore.user.role) && !authStore.isSuperAdmin) {
      next('/unauthorized')
      return
    }
  }

  next()
})

router.afterEach(() => {
  const uiStore = useUiStore()
  uiStore.stopRouteLoading()
})

router.onError(() => {
  const uiStore = useUiStore()
  uiStore.stopRouteLoading()
})

export default router
